import type { ReactNode } from "react";

import { sanitizeRichTextHtml } from "helpers/sanitize-html";

type RichTextProps = {
  html: string;
  className?: string;
  /** Rendered instead when sanitizing leaves nothing to show. */
  fallback?: ReactNode;
};

// Element styling for authored rich text, applied through arbitrary variants
// rather than a typography plugin, which this project does not install.
const RICH_TEXT_CLASS_NAME =
  "space-y-4 leading-relaxed [&_a]:font-semibold [&_a]:text-evergreen [&_a]:underline [&_a]:underline-offset-2 [&_b]:font-bold [&_em]:italic [&_i]:italic [&_li]:ml-5 [&_li]:list-disc [&_ol>li]:list-decimal [&_ol]:space-y-1 [&_p]:m-0 [&_strong]:font-bold [&_ul]:space-y-1";

/**
 * Renders sanitized authored HTML, or `fallback` when the content sanitizes to
 * nothing — which is also what happens with no DOM, so a caller relying on the
 * fallback keeps working outside the browser.
 */
export default function RichText({
  html,
  className,
  fallback = null,
}: RichTextProps) {
  const safeHtml = sanitizeRichTextHtml(html);
  if (!safeHtml) return <>{fallback}</>;

  return (
    <div
      className={
        className
          ? `${RICH_TEXT_CLASS_NAME} ${className}`
          : RICH_TEXT_CLASS_NAME
      }
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}
